/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Field, Formik } from 'formik'
import * as Yup from 'yup'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Button, Form as NXUIForm } from '@nexigendigital/nxui'
import { useResetPasswordMutation } from 'api/app'

/**********************************************************************************************************
 *   STYLE IMPORTS
 **********************************************************************************************************/
import { ResetPassword } from './form.style'

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function ResetPasswordForm() {
    const passwordResetRef = useRef()
    const navigate = useNavigate()
    const { token } = useParams()
    const [resetPassword, { isSuccess, isError }] = useResetPasswordMutation()

    function back() {
        navigate('/login', { replace: true })
    }

    useEffect(() => {
        if (isSuccess) {
            navigate('/login')
        } else if (isError) {
            passwordResetRef.current.setSubmitting(false)
        }
    }, [isError, isSuccess, navigate])

    return (
        <Formik
            innerRef={passwordResetRef}
            initialValues={{
                token,
                email: '',
                password: '',
                confirmPassword: ''
            }}
            validationSchema={Yup.object({
                email: Yup.string()
                    .email('Invalid email address')
                    .required('Required'),
                password: Yup.string()
                    .min(4)
                    .required('Required'),
                confirmPassword: Yup.string()
                    .min(4)
                    .oneOf([Yup.ref('password'), null], 'Password must match')
                    .required('Required')
            })}
            onSubmit={({ email, password, confirmPassword }) => {
                resetPassword({ token, email, password, password_confirmation: confirmPassword })
            }}
        >
            {({ isSubmitting }) => {
                return (
                    <ResetPassword.Form>
                        <ResetPassword.Body>
                            <Field
                                id={'email'}
                                label={'Email address'}
                                name={'email'}
                                type={'text'}
                                autoComplete={'username'}
                                component={NXUIForm.InputField}
                            />
                            <Field
                                id={'password'}
                                label={'Password'}
                                name={'password'}
                                type={'password'}
                                autoComplete={'current-password'}
                                component={NXUIForm.InputField}
                            />
                            <Field
                                id={'confirmPassword'}
                                label={'Confirm Password'}
                                name={'confirmPassword'}
                                type={'password'}
                                autoComplete={'current-password'}
                                component={NXUIForm.InputField}
                            />
                        </ResetPassword.Body>
                        <ResetPassword.Footer>
                            <Button type={'button'} color={'secondary'} onClick={back} disabled={isSubmitting}>Cancel</Button>
                            <Button
                                color={'primary'}
                                type={'submit'}
                                disabled={isSubmitting}
                            >
                                Reset Password
                            </Button>
                        </ResetPassword.Footer>
                    </ResetPassword.Form>
                )
            }}
        </Formik>
    )
}
